import type React from 'react';

import { Button, Link } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { StarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import type { PostPreviewSizes } from '#src/app/pages/starterDashboardV2/shared/components';
import { PostPreview } from '#src/app/pages/starterDashboardV2/shared/components';
import type { EnrichedReadyToSharePlatformPostV2 } from '#src/app/shared/api/readyToShare';

import { getCTALabelForPostType, getSectionAriaLabelForPostType, getStartSharingAriaLabelForPostType } from '../utils';

type Props = {
	onClick?: () => void;
	post: EnrichedReadyToSharePlatformPostV2;
	petition: StarterDashboardV2PageData['petition'];
	disabled: boolean;
	sharingResource: {
		previewImageUrl: string;
		message: string;
		petitionTitle: string;
	};
	previewSize: PostPreviewSizes;
};

export const PostPreviewCard: React.FC<Props> = (props) => {
	const { post, onClick, petition, sharingResource, disabled, previewSize } = props;
	const postTypeName = post.__typename;
	const { translate } = useI18n();

	const ctaCopy = disabled
		? translate('corgi.petition.starter_dashboard_v2.page_body.post_preview_card.share_from_phone')
		: getCTALabelForPostType(translate, postTypeName);

	return (
		<section
			key={postTypeName}
			className="flex gap-3"
			data-testid={`${postTypeName}-post-card`}
			aria-label={getSectionAriaLabelForPostType(translate, postTypeName)}
		>
			<div className="flex flex-col gap-3 gap-y-6">
				<Link
					data-qa="post-preview-container"
					disabled={disabled}
					onClick={onClick}
					href="#"
					variant="secondary"
					className="no-underline"
					underline={false}
					aria-disabled={disabled}
					aria-label={getStartSharingAriaLabelForPostType(translate, postTypeName)}
				>
					<PostPreview
						variant={postTypeName}
						sharingResource={sharingResource}
						size={previewSize}
						loading={false}
						showPlatformIcon
						data-testid={`${postTypeName}-post-preview`}
						petition={petition}
						disabled={disabled}
					/>
				</Link>
				<Button
					onClick={onClick}
					variant={disabled ? 'tertiary' : 'secondary'}
					size="x-small"
					disabled={disabled}
					data-qa={`${postTypeName}-share-button`}
					data-testid={`${postTypeName}-share-button`}
					aria-label={ctaCopy}
					className="w-full min-w-36 no-underline md:min-w-0"
				>
					{ctaCopy}
				</Button>
			</div>
		</section>
	);
};
